/**
 * MLX affine dequantization: turning a quantized MLX checkpoint back into floats.
 *
 * An MLX-quantized linear weight `[out_features, in_features]` is stored as three
 * safetensors tensors sharing a name prefix:
 *
 *   <p>.weight   U32,   [out_features, in_features · bits / 32]
 *   <p>.scales   float, [out_features, in_features / group_size]
 *   <p>.biases   float, same shape as scales
 *
 * and the reconstruction is affine per group: `w = q · scale + bias`, with `q` an
 * unsigned integer in `[0, 2^bits)`. Note the sign convention — MLX stores a
 * *bias*, not a zero-point, so the expression is `q·s + b` and never `s·(q − z)`.
 * MLX's own quantizer emits negative scales for roughly half of all groups (see
 * `affine_quantize` in mlx/ops.cpp: it picks whichever of w_max/w_min has the
 * larger magnitude as the anchoring edge and flips the scale's sign to suit), so
 * an implementation that assumed a non-negative scale would mirror half the
 * tensor about its bias and still produce a plausible-looking histogram.
 *
 * **The packing.** The quantized integers of a row form one contiguous bitstream,
 * least-significant bits first, laid into little-endian u32 words. For bits that
 * divide 32 (2, 4, 8) that is the same thing as "32/bits values per word, lowest
 * first". For 3, 5 and 6 it is not: values straddle word boundaries. A 3-bit
 * row's eleventh value occupies bits 30–31 of word 0 and bit 0 of word 1.
 *
 * This was read off three independent MLX backends, which all agree:
 *   - the portable fallback in `mlx/ops.cpp` (`pack_and_quantize`), which for
 *     non-power-of-two bit widths explodes each value into individual bits,
 *     LSB-first, then regroups the flat bit sequence into 32-bit words;
 *   - `mlx/backend/cpu/quantized.cpp` (`extract_bits`), byte-level and explicit:
 *     `w_out[2] = ((w_in[0] & 0xc0) >> 6) + ((w_in[1] & 0x1) << 2)` for 3-bit;
 *   - the CUDA and Metal kernels, which repeat the same byte masks.
 *
 * Group boundaries are always word-aligned even in the straddling widths, because
 * `group_size · bits` is a multiple of 32 for every supported combination (the
 * smallest group is 32 and the widest value 8 bits). Rows are therefore
 * word-aligned too, and the whole tensor is one uninterrupted bitstream.
 *
 * Supported widths are {1, 2, 3, 4, 5, 6, 8} and group sizes {32, 64, 128} — the
 * exact sets `affine_quantize` accepts. Seven bits is excluded by MLX itself.
 *
 * Everything here is pure: packed words and decoded scales in, `Float32Array` out,
 * no I/O.
 */

import { type NumericArray, floatDtypeByteWidth, roundToDtype } from "./dtype.js";

/**
 * Bit widths MLX's affine quantizer accepts. Note the absence of 7.
 *
 * **1-bit was missing, and the symptom blamed the wrong thing.** Shapes
 * constrain only the *product* `bits x groupSize`, so a tensor packing 128 of
 * them per scale is equally consistent with 1/128, 2/64 and 4/32. Omitting 1
 * from this list did not merely fail to resolve `prism-ml/Bonsai-1.7B-mlx-1bit`
 * — it made the app tell the reader that the repo's own `config.json` was
 * wrong: "config declares 1-bit, group 128, but the tensor shapes admit only
 * 4-bit/group 32 or 2-bit/group 64". The shapes admitted 1/128 perfectly well;
 * the candidate list did not.
 *
 * **Provenance, strongest first** (owner, 2026-08-08): there is an upstream MLX
 * PR adding 1-bit affine, and the owner has exercised it in another project. So
 * this is not an inference from one repo's shapes — it is a scheme that exists,
 * corroborated by a checkpoint that ships it. Worth knowing that the upstream
 * change was still a PR rather than a release when this landed: if MLX alters
 * the packing before merging, the shapes below are what would stop agreeing, and
 * this is the comment to come back to.
 *
 * Verified against that repo's real headers: `embed_tokens` is `[151669, 64]`
 * u32 with `[151669, 16]` scales, and `64 x 32 = 2048` is exactly
 * `hidden_size x 1 bit`, with `2048 / 16 = 128` values per group. `mlp.down_proj`
 * agrees independently at `[2048, 192]` / `[2048, 48]` — 6144 x 1 bit, group 128.
 *
 * The packing path needed nothing: `readBits` masks with `(1 << bits) - 1`, and
 * a 1-bit value can never straddle a word boundary, so it takes the same branch
 * every other width already does.
 */
export const AFFINE_BITS: readonly number[] = [1, 2, 3, 4, 5, 6, 8];

/** Group sizes MLX's affine quantizer accepts. */
export const AFFINE_GROUP_SIZES: readonly number[] = [32, 64, 128];

/** Bits per u32 word. Named because it appears in every shape identity below. */
const BITS_PER_WORD = 32;

/**
 * Refuse to materialize more than this many floats in one call. 2^28 elements is
 * a 1 GiB `Float32Array` — already far past what a browser tab should attempt,
 * and comfortably above any sampled read the app issues. Without the cap a
 * mis-derived `rows`/`cols` pair turns a typo into an allocation failure whose
 * message says nothing about where it came from.
 */
export const MAX_DEQUANTIZED_ELEMENTS = 1 << 28;

export function isSupportedAffineBits(bits: number): boolean {
  return AFFINE_BITS.includes(bits);
}

export function isSupportedAffineGroupSize(groupSize: number): boolean {
  return AFFINE_GROUP_SIZES.includes(groupSize);
}

/**
 * Read `bits` bits starting at bit index `bitStart` of a little-endian u32 stream.
 *
 * The `?? 0` on each word is unreachable in every caller here — both validate
 * lengths against the shape identities first — but `noUncheckedIndexedAccess` is
 * on and a non-null assertion would be a worse way to say the same thing.
 *
 * The two-word branch is the whole reason 3/5/6-bit exist as a separate problem.
 * `available = 32 - offset` is how many of the value's low bits live in the first
 * word; the rest come from the low `bits - available` bits of the next word,
 * shifted up into place. JS bitwise operators work on int32, so the intermediate
 * OR can go negative — the trailing `& mask` (mask ≤ 255) resolves that back to a
 * non-negative value with the correct bits.
 */
function readBits(packed: Uint32Array, bitStart: number, bits: number): number {
  const word = bitStart >>> 5;
  const offset = bitStart & 31;
  const mask = (1 << bits) - 1;
  const low = (packed[word] ?? 0) >>> offset;
  const available = BITS_PER_WORD - offset;
  if (available >= bits) return low & mask;
  return (low | ((packed[word + 1] ?? 0) << available)) & mask;
}

/**
 * Extract the quantized integers from a packed u32 payload.
 *
 * `Uint8Array` rather than `Uint16Array`: every supported width is ≤ 8 bits, so a
 * byte holds any `q` exactly and the intermediate costs half the memory of the
 * wider array — which matters, because this sits alongside a `Float32Array` four
 * times its size.
 *
 * The whole input is treated as one bitstream. That is equivalent to unpacking
 * row by row only because rows are word-aligned (see the module note), so this
 * function is safe to hand a whole tensor, a run of complete rows, or a single
 * row — but *not* a range cut at an arbitrary word, which would shift the phase
 * of every value after the cut.
 */
export function unpackAffine(packed: Uint32Array, bits: number): Uint8Array {
  if (!isSupportedAffineBits(bits)) {
    throw new Error(
      `MLX affine unpacking supports ${AFFINE_BITS.join(", ")} bits; got ${bits}. (MLX itself rejects 7-bit, and anything above 8 does not fit its packing.)`,
    );
  }
  if (packed.length > MAX_DEQUANTIZED_ELEMENTS / 4) {
    throw new Error(
      `Refusing to unpack ${packed.length} u32 words in one call — above the ${MAX_DEQUANTIZED_ELEMENTS / 4}-word cap. Read the tensor in row-aligned slices instead.`,
    );
  }

  const totalBits = packed.length * BITS_PER_WORD;
  if (totalBits % bits !== 0) {
    throw new Error(
      `${packed.length} u32 words hold ${totalBits} bits, which is not a whole number of ${bits}-bit values — the slice is cut mid-value or the bit width is wrong`,
    );
  }

  const count = totalBits / bits;
  const out = new Uint8Array(count);
  for (let i = 0; i < count; i += 1) {
    out[i] = readBits(packed, i * bits, bits);
  }
  return out;
}

export interface DequantizeAffineInput {
  /** Packed u32 words, exactly `rows × cols × bits / 32` of them. */
  readonly packed: Uint32Array;
  /** One scale per (row, group), row-major: `rows × cols / groupSize`. */
  readonly scales: NumericArray;
  /** One bias per (row, group), same layout as `scales`. */
  readonly biases: NumericArray;
  readonly bits: number;
  readonly groupSize: number;
  /** Rows *in this buffer* — a sampled run of rows is a legitimate input. */
  readonly rows: number;
  /** Unpacked columns, i.e. `in_features`, not the packed word count. */
  readonly cols: number;
  /**
   * Storage dtype of the scale/bias planes — `"F16"` for essentially every MLX
   * repo in the wild.
   *
   * Load-bearing, not cosmetic. `mx.dequantize` returns the dtype of the
   * planes, so a model with f16 scales holds **f16** weights at runtime.
   * Computing `q · s + b` in f32 and stopping there reports a distribution
   * finer than the model actually has — smooth where reality sits on f16 steps.
   * Confirmed against real `mx` output: without this rounding, 7 of the 10
   * golden cases differ, all of them in the last few mantissa bits.
   *
   * Defaults to `"F16"` because that is what MLX writes; pass the real plane
   * dtype when it is known.
   */
  readonly planeDtype?: string | undefined;
}

/**
 * Dequantize an MLX affine payload to `[rows, cols]` floats, row-major.
 *
 * `rows` is the row count *of the buffer handed in*, not of the tensor on disk:
 * the app samples complete rows, and a run of rows is self-contained here because
 * rows are word-aligned and each row's scales and biases are contiguous. The
 * caller must slice `scales` and `biases` to the same rows, and every check below
 * is really a check that it did.
 *
 * Output is f32 while the accumulation `q·s + b` happens in f64 (all JS numbers
 * are), which is strictly more precise than MLX's own kernels — they accumulate
 * in the scale's dtype, usually f16. The difference is at most an f16 ULP and
 * always in the direction of being closer to the intended value, so it cannot
 * manufacture structure that is not there.
 *
 * Non-finite scales or biases propagate to NaN rather than being filtered. That
 * is deliberate: a NaN scale is a corrupt checkpoint, and `analyzeMoments`
 * already counts and reports non-finite elements. Silently dropping them here
 * would hide the corruption behind a clean-looking histogram.
 */
export function dequantizeAffine(input: DequantizeAffineInput): Float32Array {
  const { packed, scales, biases, bits, groupSize, rows, cols } = input;
  const planeDtype = input.planeDtype ?? "F16";

  if (!isSupportedAffineBits(bits)) {
    throw new Error(
      `MLX affine dequantization supports ${AFFINE_BITS.join(", ")} bits; got ${bits}. (MLX itself rejects 7-bit, and anything above 8 does not fit its packing.)`,
    );
  }
  // The group size is checked against MLX's own list rather than merely "divides
  // cols". It is not recoverable from the packed bytes, so a wrong value here
  // does not fail — it silently reassigns scales to the wrong weights and
  // produces a tensor that looks fine and is wrong everywhere.
  if (!isSupportedAffineGroupSize(groupSize)) {
    throw new Error(
      `MLX affine group size must be one of ${AFFINE_GROUP_SIZES.join(", ")}; got ${groupSize}`,
    );
  }
  if (!Number.isInteger(rows) || rows < 0 || !Number.isInteger(cols) || cols < 0) {
    throw new Error(
      `Dequantization needs non-negative integer rows and cols; got rows=${rows}, cols=${cols}`,
    );
  }
  if (cols % groupSize !== 0) {
    throw new Error(
      `${cols} columns is not a whole number of ${groupSize}-element groups — MLX cannot quantize a row whose length is not a multiple of the group size, so one of the two is wrong`,
    );
  }
  if ((cols * bits) % BITS_PER_WORD !== 0) {
    throw new Error(
      `${cols} columns at ${bits} bits is ${cols * bits} bits per row, which is not a whole number of u32 words — rows would not be word-aligned`,
    );
  }
  if (rows * cols > MAX_DEQUANTIZED_ELEMENTS) {
    throw new Error(
      `Refusing to dequantize ${rows}×${cols} = ${rows * cols} weights in one call — above the ${MAX_DEQUANTIZED_ELEMENTS}-element cap (${MAX_DEQUANTIZED_ELEMENTS / 2 ** 28} GiB as f32). Sample rows instead.`,
    );
  }

  const wordsPerRow = (cols * bits) / BITS_PER_WORD;
  const groupsPerRow = cols / groupSize;
  const expectedWords = rows * wordsPerRow;
  const expectedGroups = rows * groupsPerRow;

  if (packed.length !== expectedWords) {
    throw new Error(
      `Packed payload has ${packed.length} u32 words but ${rows}×${cols} at ${bits} bits needs exactly ${expectedWords} — the shape, the bit width, or the byte range is wrong`,
    );
  }
  if (scales.length !== expectedGroups) {
    throw new Error(
      `Expected ${expectedGroups} scales for ${rows} rows of ${groupsPerRow} groups; got ${scales.length}`,
    );
  }
  if (biases.length !== expectedGroups) {
    throw new Error(
      `Expected ${expectedGroups} biases for ${rows} rows of ${groupsPerRow} groups; got ${biases.length}`,
    );
  }

  const out = new Float32Array(rows * cols);
  const mask = (1 << bits) - 1;
  // Hoisted: `roundToDtype` branches on a string, and this is the innermost
  // loop over every weight in the tensor.
  const needsRounding = planeDtype === "F16" || planeDtype === "BF16";

  for (let r = 0; r < rows; r += 1) {
    // Bit offsets are taken relative to the row, not the tensor. A 3072-column
    // 8-bit row is 24576 bits, so `bitStart >>> 5` inside `readBits` stays far
    // inside int32 — whereas a whole-tensor bit index can exceed 2^32 on a large
    // embedding table, where `>>>` would silently wrap.
    const rowWords = packed.subarray(r * wordsPerRow, (r + 1) * wordsPerRow);
    const scaleBase = r * groupsPerRow;
    const outBase = r * cols;

    for (let g = 0; g < groupsPerRow; g += 1) {
      const scale = scales[scaleBase + g] ?? 0;
      const bias = biases[scaleBase + g] ?? 0;
      const colBase = g * groupSize;
      let bitStart = colBase * bits;

      for (let k = 0; k < groupSize; k += 1) {
        // Inlined rather than calling readBits: this is the innermost loop over
        // every weight in the tensor, and hoisting the mask and reusing the
        // running `bitStart` avoids two multiplications per element.
        const word = bitStart >>> 5;
        const offset = bitStart & 31;
        const low = (rowWords[word] ?? 0) >>> offset;
        const available = BITS_PER_WORD - offset;
        const q =
          available >= bits
            ? low & mask
            : (low | ((rowWords[word + 1] ?? 0) << available)) & mask;

        // `q * scale + bias` evaluates in f64 and is stored once, so the
        // product is not double-rounded — matching the kernel's FMA. The
        // result is then rounded to the plane dtype, because that is the
        // precision the model actually holds.
        const value = q * scale + bias;
        out[outBase + colBase + k] = needsRounding
          ? roundToDtype(value, planeDtype)
          : value;
        bitStart += bits;
      }
    }
  }

  return out;
}

export interface EffectiveBitsInput {
  readonly bits: number;
  readonly groupSize: number;
  /** Safetensors dtype of the `.scales` plane, e.g. `"F16"` or `"BF16"`. */
  readonly scaleDtype: string;
  /**
   * Dtype of the `.biases` plane. Omit for a scheme that carries scales only —
   * MLX's mxfp4 mode is one — which halves the metadata overhead.
   */
  readonly biasDtype?: string | undefined;
}

/**
 * The bits actually spent per weight, scales and biases included.
 *
 * A "4-bit" MLX model is never 4 bits per weight, and the gap is not small. Each
 * group of `groupSize` weights carries one scale and one bias in a *float* dtype,
 * so their cost amortized over the group is
 *
 *     8 · (scaleBytes + biasBytes) / groupSize   bits per weight
 *
 * on top of the `bits` the packed payload spends. At 4 bits, group 32, with f16
 * scales and biases that is 4 + 8·(2+2)/32 = 4 + 1 = **5.0 bpw** — a 25% surcharge
 * over the advertised number. Widening the group to 64 halves the surcharge to
 * 4.5; at group 128 it is 4.25. Conversely an 8-bit model at group 32 pays the
 * same absolute 1.0 bits, which is only a 12.5% surcharge — the overhead is
 * constant in `bits`, so it hurts the aggressive quantizations most, which is
 * exactly where people are counting bytes.
 *
 * This is per *quantized* tensor. A repo's whole-file average is higher again,
 * because norms, biases and any layer left unquantized are stored at full width —
 * that ledger belongs to the inventory, not here.
 *
 * The scale dtype must be float-decodable. Refusing an integer dtype is not
 * pedantry: it would mean the tensor is not what this function was told it is,
 * and returning a number anyway would put a confident wrong bpw on screen.
 */
export function effectiveBitsPerWeight(input: EffectiveBitsInput): number {
  const { bits, groupSize, scaleDtype, biasDtype } = input;

  if (!isSupportedAffineBits(bits)) {
    throw new Error(
      `MLX affine bit width must be one of ${AFFINE_BITS.join(", ")}; got ${bits}`,
    );
  }
  if (!isSupportedAffineGroupSize(groupSize)) {
    throw new Error(
      `MLX affine group size must be one of ${AFFINE_GROUP_SIZES.join(", ")}; got ${groupSize}`,
    );
  }

  const scaleBytes = floatDtypeByteWidth(scaleDtype);
  if (scaleBytes === undefined) {
    throw new Error(
      `Scale plane dtype ${scaleDtype} is not a float type — MLX stores scales in the original weight dtype, so this is not an MLX affine triple`,
    );
  }

  let biasBytes = 0;
  if (biasDtype !== undefined) {
    const width = floatDtypeByteWidth(biasDtype);
    if (width === undefined) {
      throw new Error(
        `Bias plane dtype ${biasDtype} is not a float type — MLX stores biases in the original weight dtype, so this is not an MLX affine triple`,
      );
    }
    biasBytes = width;
  }

  return bits + (8 * (scaleBytes + biasBytes)) / groupSize;
}

/** A (bits, groupSize) pair consistent with a triple's shapes, and the `cols` it implies. */
export interface AffineShapeCandidate {
  readonly bits: number;
  readonly groupSize: number;
  /** Unpacked columns — `in_features` — under this interpretation. */
  readonly cols: number;
}

/**
 * Every (bits, groupSize) the shapes alone permit.
 *
 * **The shapes do not always determine the scheme, and this is the surprise in
 * the format.** The two observable widths give one equation:
 *
 *     cols = groupSize · scaleCols        and        packedCols = cols · bits / 32
 *     ⇒  32 · packedCols / scaleCols = bits · groupSize
 *
 * — a single constraint on the *product*. `in_features` is not recorded anywhere
 * in the triple, so a weight of `[896, 112]` with scales `[896, 14]` fixes only
 * `bits · groupSize = 256`, which is satisfied by (8, 32), (4, 64) and (2, 128)
 * alike. Those are three completely different models and all three unpack
 * without error; only one is right.
 *
 * Some products are unambiguous — 64 → (2,32), 96 → (3,32), 160/320/640 → 5-bit,
 * 768 → (6,128), 1024 → (8,128) — and for those the shapes really are enough.
 * For the rest the config's declaration is the only source of truth, which is why
 * `findAffineGroups` takes it as an input rather than deriving everything and
 * cross-checking afterwards. Returning the full candidate list keeps that honest:
 * a caller with no config gets told there is a choice instead of a guess.
 *
 * Returned in descending `bits` order, so the first entry is the largest bit
 * width the shapes allow — useful only for display, never as a default.
 */
export function affineShapeCandidates(
  packedCols: number,
  scaleCols: number,
): AffineShapeCandidate[] {
  if (
    !Number.isInteger(packedCols) ||
    !Number.isInteger(scaleCols) ||
    packedCols <= 0 ||
    scaleCols <= 0
  ) {
    return [];
  }
  const product = (BITS_PER_WORD * packedCols) / scaleCols;
  if (!Number.isInteger(product)) return [];

  const candidates: AffineShapeCandidate[] = [];
  for (const bits of AFFINE_BITS) {
    for (const groupSize of AFFINE_GROUP_SIZES) {
      if (bits * groupSize === product) {
        candidates.push({ bits, groupSize, cols: groupSize * scaleCols });
      }
    }
  }
  return candidates.sort((a, b) => b.bits - a.bits);
}

/** The minimum a tensor inventory entry has to expose to be classified. */
export interface TensorLike {
  readonly name: string;
  readonly dtype: string;
  readonly shape: readonly number[];
}

/**
 * What a `config.json` claims. Either field alone is enough to disambiguate.
 *
 * `overrides` carries the *per-module* entries MLX writes into the same
 * `quantization` block — see `classFor` for why their structure is trusted even
 * though their numbers are not.
 */
export interface DeclaredQuantization {
  readonly bits?: number | undefined;
  readonly groupSize?: number | undefined;
  /**
   * `quant_method` verbatim, when the config states one.
   *
   * Carried because for some schemes the *name* is the entire declaration:
   * MXFP4 repos state `{"quant_method": "mxfp4"}` with no `bits` and no
   * `group_size`, since the format specifies both. Nothing here resolves
   * anything from it — `block-scaled.ts` uses it to decide whether a pair of
   * shapes is even eligible for the check it then performs.
   */
  readonly method?: string | undefined;
  /**
   * Per-module entries, keyed by module path exactly as they appear in the
   * config (`model.embed_tokens`, `lm_head`, …).
   *
   * `null` means the module is declared *unquantized* — MLX writes `false` for
   * a layer its predicate skipped. A nested object means "this module was
   * quantized with different settings from the global ones"; fields it omits
   * fall back to the global declaration.
   */
  readonly overrides?:
    | Readonly<Record<string, DeclaredQuantization | null>>
    | undefined;
  /**
   * Every distinct `{bits, group}` pair the config states, when it states more
   * than one thing.
   *
   * A mixed-precision repo declares a *set*, not a number:
   * `kaitchup/Qwen3.6-27B-autoscheme-3.5bit-XS` quantizes attention at 4 bits
   * and most MLP at 3, in three `config_groups`. `bits` and `groupSize` above
   * can only carry one of those, and carrying either would be a claim about
   * every tensor the other describes — so they stay `undefined` and the
   * alternatives come through here.
   *
   * This is not a second declaration to prefer over the first. It is the
   * candidate list construction chooses *from*: a folder tries each against the
   * shapes and keeps a pair only when exactly one survives. See
   * `compressed-tensors.ts`, which is the only consumer today.
   */
  readonly candidates?: readonly QuantizationCandidate[] | undefined;
}

/** One complete `{bits, group}` claim — both numbers, or it is not a candidate. */
export interface QuantizationCandidate {
  readonly bits: number;
  readonly groupSize: number;
}

/**
 * Architectural facts from `config.json` that fix a tensor's *logical* width.
 *
 * These are not quantization settings and are not part of the repo's claim
 * about how it was quantized — they describe the model, and they are stated by
 * every checkpoint whether or not it is quantized. That independence is exactly
 * what makes them useful here: see `readAnchor`.
 */
export interface ArchitectureAnchors {
  /**
   * The residual-stream width. It is the *column* count of the token embedding
   * table and of `lm_head`, which is the whole point — those are the two
   * tensors whose logical width is known without reading a single weight byte.
   */
  readonly hiddenSize?: number | undefined;
  /**
   * The token count, i.e. the *row* count of both of those tensors. Used only
   * as a sanity check on the name match, never to derive a group size.
   */
  readonly vocabSize?: number | undefined;
  /**
   * Attention head count, and the rank of a factored Q projection where the
   * config declares one.
   *
   * Not used by the MLX arithmetic at all — they are here because this is
   * already the type that means "facts `config.json` states about the
   * architecture, carried alongside the tensors", and `anatomy.ts`'s shape
   * resolver needs exactly that to turn a detected factored pair into a named
   * position. A second anchors type would be a second thing to thread through
   * `toLogicalTensors` and keep in step.
   */
  readonly heads?: number | undefined;
  readonly qLoraRank?: number | undefined;
  /**
   * Per-head width, when declared.
   *
   * Carried because `heads` alone cannot check a fan-out. The resolver used to
   * test `fanOut % heads === 0`, which most fan-outs satisfy — a divisibility
   * test dressed as an identity. `heads * headDim` is the number the fan-out
   * has to *be*.
   */
  readonly headDim?: number | undefined;
  /**
   * The other factored rank a config may declare.
   *
   * Present so the resolver can tell "this rank names Q" from "this rank names
   * something, and Q is one of the candidates". On `DeepSeek-V4-Flash`
   * `q_lora_rank === kv_lora_rank === 1024`, so a rank of 1024 identifies
   * nothing — and naming it `attn.q` would be a mislabelled cell, which this
   * project holds to be worse than an unnamed one.
   */
  readonly kvLoraRank?: number | undefined;
}

/**
 * How a group's `resolved` scheme was arrived at, so the UI can show its working.
 *
 * The ordering is not a precedence: `"anchor"` and `"shapes"` are both measured
 * off real tensor dimensions, `"declaration"` is the repo's word for it.
 */
export type AffineEvidence = "shapes" | "anchor" | "anchor-propagated" | "declaration";

/** One `<prefix>.{weight,scales,biases}` triple, resolved as far as the evidence allows. */
export interface AffineGroup {
  /** The shared name prefix, e.g. `model.layers.0.mlp.gate_proj`. */
  readonly prefix: string;
  readonly weightName: string;
  readonly scalesName: string;
  readonly biasesName: string;
  /** Product of the leading dims — MoE expert stacks are 3-D, so this is not shape[0]. */
  readonly rows: number;
  /** Last dim of the packed weight, in u32 words per row. */
  readonly packedCols: number;
  /** Last dim of the scales plane, i.e. groups per row. */
  readonly scaleCols: number;
  readonly scaleDtype: string;
  readonly biasDtype: string;
  /** Every (bits, groupSize) the shapes permit; see `affineShapeCandidates`. */
  readonly candidates: readonly AffineShapeCandidate[];
  /**
   * The single candidate to use, or `undefined` when the evidence does not pick
   * one — either the shapes are ambiguous and nothing was declared, or the
   * declaration contradicts the shapes. Never a guess.
   */
  readonly resolved: AffineShapeCandidate | undefined;
  /** Effective bpw for `resolved`, or `undefined` when unresolved. */
  readonly bitsPerWeight: number | undefined;
  /**
   * Set when two sources of evidence cannot both be true — the declaration
   * against the shapes, or an anchor against the declaration.
   */
  readonly disagreement: string | undefined;
  /** What produced `resolved`; `undefined` whenever `resolved` is. */
  readonly evidence: AffineEvidence | undefined;
}

/** A prefix that looked like a triple but is not one, and why. */
export interface AffineRejection {
  readonly prefix: string;
  readonly reason: string;
}

export interface AffineScan {
  readonly groups: readonly AffineGroup[];
  /**
   * Near-misses: a `.scales` sibling exists but the triple does not hold up.
   * Surfaced rather than dropped — "this repo has 40 scales planes and no biases"
   * is a diagnosis (mxfp4, or a non-MLX scheme), not an absence of results.
   */
  readonly rejections: readonly AffineRejection[];
}

/**
 * Find the MLX affine triples in a tensor inventory.
 *
 * Detection is by the sibling naming convention — `<p>.weight` with `<p>.scales`
 * and `<p>.biases` alongside — because that is what MLX's own loader keys on
 * (`mlx_lm/utils.py` decides whether to quantize a layer with `f"{p}.scales" in
 * weights`). Structure is then checked against the shape identities, so a tensor
 * that merely happens to have a `.scales` neighbour cannot slip through.
 *
 * A `.scales` plane with no `.biases` is *not* an affine triple. MLX's mxfp4 mode
 * emits exactly that, and dequantizing it with an implied zero bias would give
 * numbers that are wrong by an offset per group — plausible, unflagged, wrong. It
 * lands in `rejections` instead.
 *
 * `declared` is the repo's own claim, from `config.json`'s `quantization` block.
 * When it agrees with the shapes it disambiguates them; when it contradicts them
 * neither wins — `resolved` stays `undefined` and `disagreement` says so. That is
 * the fail-loud rule: a repo whose config says 4-bit while its shapes can only be
 * 3-bit has something genuinely wrong with it, and quietly preferring one source
 * would erase the finding. Pass it **only when the repo really declares a
 * quantization block**: its presence is also what tells this function the
 * per-module partition is knowable, which is what licenses propagation below.
 *
 * `anchors` are architectural facts — `hidden_size`, `vocab_size` — from the same
 * `config.json`. They are not a claim about quantization, and they resolve the
 * scheme *by measurement* rather than by trust; see `readAnchor` and
 * `propagationSources`.
 */
export function findAffineGroups(
  tensors: readonly TensorLike[],
  declared?: DeclaredQuantization,
  anchors?: ArchitectureAnchors,
): AffineScan {
  const byName = new Map<string, TensorLike>();
  for (const t of tensors) byName.set(t.name, t);

  const drafts: AffineDraft[] = [];
  const rejections: AffineRejection[] = [];

  for (const scalesTensor of tensors) {
    if (!scalesTensor.name.endsWith(".scales")) continue;
    const prefix = scalesTensor.name.slice(0, -".scales".length);

    const weight = byName.get(`${prefix}.weight`);
    const biases = byName.get(`${prefix}.biases`);

    if (weight === undefined) {
      rejections.push({
        prefix,
        reason: `${prefix}.scales exists but ${prefix}.weight does not — nothing to dequantize`,
      });
      continue;
    }
    if (biases === undefined) {
      rejections.push({
        prefix,
        reason: `${prefix}.scales exists but ${prefix}.biases does not. MLX affine always stores both; a scales-only plane means a different scheme (mxfp4 stores scales alone), and assuming a zero bias would offset every group.`,
      });
      continue;
    }
    if (weight.dtype !== "U32") {
      rejections.push({
        prefix,
        reason: `${prefix}.weight is ${weight.dtype}, not U32 — MLX packs affine payloads into u32 words`,
      });
      continue;
    }
    if (
      floatDtypeByteWidth(scalesTensor.dtype) === undefined ||
      floatDtypeByteWidth(biases.dtype) === undefined
    ) {
      rejections.push({
        prefix,
        reason: `scales/biases are ${scalesTensor.dtype}/${biases.dtype}; MLX stores both in the original float dtype`,
      });
      continue;
    }

    const wShape = weight.shape;
    const sShape = scalesTensor.shape;
    if (wShape.length < 2 || wShape.length !== sShape.length) {
      rejections.push({
        prefix,
        reason: `weight shape [${wShape.join(", ")}] and scales shape [${sShape.join(", ")}] must have the same rank, at least 2`,
      });
      continue;
    }
    if (sShape.join() !== biases.shape.join()) {
      rejections.push({
        prefix,
        reason: `scales [${sShape.join(", ")}] and biases [${biases.shape.join(", ")}] must have identical shapes`,
      });
      continue;
    }
    // Leading dims are the row axis and must match exactly. They are collapsed
    // rather than required to be 2-D because MoE checkpoints stack experts:
    // `switch_mlp.down_proj.weight` is genuinely [experts, out_features, packed].
    const wLead = wShape.slice(0, -1);
    const sLead = sShape.slice(0, -1);
    if (wLead.join() !== sLead.join()) {
      rejections.push({
        prefix,
        reason: `weight rows [${wLead.join(", ")}] and scales rows [${sLead.join(", ")}] disagree`,
      });
      continue;
    }

    const packedCols = wShape[wShape.length - 1] ?? 0;
    const scaleCols = sShape[sShape.length - 1] ?? 0;
    const candidates = affineShapeCandidates(packedCols, scaleCols);
    if (candidates.length === 0) {
      rejections.push({
        prefix,
        reason: `no supported (bits, group size) explains packed width ${packedCols} against ${scaleCols} groups — 32·${packedCols}/${scaleCols} is not bits × groupSize for any of ${AFFINE_BITS.join("/")} bits and ${AFFINE_GROUP_SIZES.join("/")} groups`,
      });
      continue;
    }

    drafts.push({
      prefix,
      weightName: weight.name,
      scalesName: scalesTensor.name,
      biasesName: biases.name,
      rows: wLead.reduce((a, b) => a * b, 1),
      rank: wShape.length,
      packedCols,
      scaleCols,
      scaleDtype: scalesTensor.dtype,
      biasDtype: biases.dtype,
      candidates,
      // bits × groupSize, straight from the shape identity. Identical for every
      // tensor quantized with the same settings, whatever its dimensions —
      // which is what makes it a usable check on whether two modules really do
      // share a scheme. `candidates` being non-empty is what proves it whole.
      product: (BITS_PER_WORD * packedCols) / scaleCols,
    });
  }

  return { groups: resolveDrafts(drafts, declared, anchors), rejections };
}

/** A triple that passed every structural check, before the scheme is pinned down. */
interface AffineDraft {
  readonly prefix: string;
  readonly weightName: string;
  readonly scalesName: string;
  readonly biasesName: string;
  readonly rows: number;
  /** Rank of the packed weight. Anchors apply to 2-D tensors only. */
  readonly rank: number;
  readonly packedCols: number;
  readonly scaleCols: number;
  readonly scaleDtype: string;
  readonly biasDtype: string;
  readonly candidates: readonly AffineShapeCandidate[];
  readonly product: number;
}

/**
 * Anchor tensors: the ones whose logical column count is known from the config.
 *
 * An embedding table is `[vocab_size, hidden_size]` and an untied `lm_head` is
 * `[vocab_size, hidden_size]` too, so for both the *columns* are `hidden_size` —
 * a number `config.json` states outright, in a field that has nothing to do with
 * quantization and is present whether or not the repo is quantized.
 *
 * Suffix-anchored (`(?:^|\.)x$`) rather than the loose word matches the anatomy
 * grid uses, because a false positive here does not merely mislabel a cell: it
 * would hand the resolver a logical width that belongs to some other tensor.
 * `vision_tower.embeddings.patch_embedding` must not look like a token embedding.
 */
const ANCHOR_RULES: ReadonlyArray<{ readonly pattern: RegExp; readonly what: string }> =
  [
    {
      pattern: /(?:^|\.)(?:embed_tokens|wte|word_embeddings|tok_embeddings|embed_in)$/,
      what: "a token embedding table",
    },
    {
      pattern: /(?:^|\.)(?:lm_head|embed_out)$|^output$/,
      what: "the output projection",
    },
  ];

/** Anchor evidence for one group, and the sentence that explains where it came from. */
interface AnchorEvidence {
  readonly candidate: AffineShapeCandidate;
  readonly evidence: "anchor" | "anchor-propagated";
  /** Prose account of the derivation, reused verbatim in `disagreement`. */
  readonly basis: string;
}

/**
 * Resolve a group from an architectural anchor, or `undefined` if it cannot.
 *
 * **This is the one place the ambiguity actually breaks.** The stored shapes give
 * a single equation in two unknowns (`32·packedCols/scaleCols = bits·groupSize`),
 * so they fix only the product. But a tensor whose logical width is known from
 * outside the triple supplies the missing second equation directly:
 *
 *     groupSize = logicalCols / scaleCols
 *
 * and with `groupSize` known, `bits = product / groupSize` follows. For an
 * embedding table or an `lm_head`, `logicalCols` is `hidden_size` — free, already
 * in a config we may already have fetched, and stated by the model author for
 * reasons that have nothing to do with quantization. So an embedding stored as
 * `[151936, 112]` against `[151936, 14]` scales — which the shapes alone leave as
 * 8-bit/g32, 4-bit/g64 or 2-bit/g128, since they fix only `bits·groupSize = 256`
 * — resolves the moment `hidden_size = 896` is known: 896/14 = 64, so group 64,
 * so 4-bit, and the other two readings are excluded rather than ranked.
 *
 * Every check below is a reason to *decline* rather than to force:
 *
 *  - the name must end at an anchor module, not merely contain one;
 *  - the tensor must be 2-D — a 3-D match is a stacked expert bank, not an
 *    embedding, and its last axis is not `hidden_size`;
 *  - `rows` must be at least `vocab_size` when that is known. Checkpoints pad
 *    the vocabulary axis up to a round number and never down, so `rows` below
 *    `vocab_size` means the name matched something that is not the embedding;
 *  - `scaleCols` must divide `hidden_size` exactly, the quotient must be one of
 *    MLX's group sizes, and it must select one of the shapes' own candidates.
 *
 * A repo whose `hidden_size` implies group 48, or does not divide at all, gets no
 * anchor — not a rounded one. The anchor is an *identification* of the tensor,
 * and an identification that fails its own consistency checks is worth nothing.
 */
function readAnchor(
  draft: AffineDraft,
  anchors: ArchitectureAnchors | undefined,
): AnchorEvidence | undefined {
  if (anchors === undefined) return undefined;
  const hiddenSize = anchors.hiddenSize;
  if (hiddenSize === undefined || !Number.isInteger(hiddenSize) || hiddenSize <= 0) {
    return undefined;
  }

  const what = ANCHOR_RULES.find((r) => r.pattern.test(draft.prefix))?.what;
  if (what === undefined) return undefined;
  if (draft.rank !== 2) return undefined;

  const vocabSize = anchors.vocabSize;
  if (
    vocabSize !== undefined &&
    Number.isInteger(vocabSize) &&
    draft.rows < vocabSize
  ) {
    return undefined;
  }

  if (hiddenSize % draft.scaleCols !== 0) return undefined;
  const groupSize = hiddenSize / draft.scaleCols;
  if (!isSupportedAffineGroupSize(groupSize)) return undefined;

  const candidate = draft.candidates.find((c) => c.groupSize === groupSize);
  if (candidate === undefined) return undefined;

  return {
    candidate,
    evidence: "anchor",
    basis: `${draft.prefix} is ${what}, so its logical width is config hidden_size = ${hiddenSize}; ${hiddenSize} columns over ${draft.scaleCols} scale groups is group ${groupSize}, which leaves ${candidate.bits}-bit as the only reading of the shapes`,
  };
}

/** The global class key. Not a valid module path, so it cannot collide with one. */
const GLOBAL_CLASS = "*";

/**
 * Which declaration governs a module, and which propagation class it belongs to.
 *
 * MLX records per-module exceptions inside the very same `quantization` block —
 * `{"group_size": 64, "bits": 4, "model.embed_tokens": {"group_size": 32,
 * "bits": 8}, "lm_head": false}` — because **its own loader needs them**:
 * `mlx_lm` rebuilds the quantized modules by consulting that block per path, so a
 * repo whose partition were wrong would not load at all. That asymmetry is the
 * whole justification for the propagation rule: the block's *structure* (which
 * modules were treated specially) is load-bearing and self-verifying, while its
 * *numbers* are the thing we decline to take on faith. Reading the partition is
 * therefore a much weaker act of trust than reading the bit width.
 *
 * Longest-match wins, on dot boundaries, so an entry for `model.layers.3` covers
 * `model.layers.3.mlp.down_proj` but an entry for `model.layers.3` never
 * accidentally swallows `model.layers.30`.
 */
function classFor(
  prefix: string,
  declared: DeclaredQuantization | undefined,
): { readonly key: string; readonly declared: DeclaredQuantization | undefined } {
  if (declared === undefined) return { key: GLOBAL_CLASS, declared: undefined };

  const overrides = declared.overrides;
  let bestKey: string | undefined;
  if (overrides !== undefined) {
    for (const key of Object.keys(overrides)) {
      if (key !== prefix && !prefix.startsWith(`${key}.`)) continue;
      if (bestKey === undefined || key.length > bestKey.length) bestKey = key;
    }
  }

  if (bestKey === undefined) {
    return {
      key: GLOBAL_CLASS,
      declared: { bits: declared.bits, groupSize: declared.groupSize },
    };
  }

  const entry = overrides?.[bestKey] ?? null;
  if (entry === null) {
    // The config says this module was left unquantized, yet here it is with a
    // full affine triple. Something is off, so it gets its own class and no
    // declaration: nothing propagates in, nothing propagates out.
    return { key: `!${bestKey}`, declared: undefined };
  }
  return {
    key: bestKey,
    declared: {
      bits: entry.bits ?? declared.bits,
      groupSize: entry.groupSize ?? declared.groupSize,
    },
  };
}

interface ResolveOutcome {
  readonly resolved: AffineShapeCandidate | undefined;
  readonly disagreement: string | undefined;
  readonly evidence: AffineEvidence | undefined;
}

/**
 * Combine the three sources of evidence for one group.
 *
 * With an anchor present the anchor is what we *act on* — it is derived from
 * measured dimensions plus an architectural constant, so it stands on its own
 * and the declaration merely corroborates it. But when the two disagree, the
 * anchor does not quietly win: silently overriding the config is the same
 * "one source wins without saying so" failure as silently trusting it, and the
 * disagreement is itself the most interesting thing about that repo. Both are
 * named, and the group stays unresolved.
 */
function resolveOne(
  candidates: readonly AffineShapeCandidate[],
  declared: DeclaredQuantization | undefined,
  anchor: AnchorEvidence | undefined,
): ResolveOutcome {
  const wantBits = declared?.bits;
  const wantGroup = declared?.groupSize;

  if (anchor === undefined) {
    const base = resolveCandidate(candidates, declared);
    if (base.resolved === undefined) return { ...base, evidence: undefined };
    const declaredSomething = wantBits !== undefined || wantGroup !== undefined;
    return { ...base, evidence: declaredSomething ? "declaration" : "shapes" };
  }

  const agrees =
    (wantBits === undefined || wantBits === anchor.candidate.bits) &&
    (wantGroup === undefined || wantGroup === anchor.candidate.groupSize);
  if (agrees) {
    return {
      resolved: anchor.candidate,
      disagreement: undefined,
      evidence: anchor.evidence,
    };
  }

  return {
    resolved: undefined,
    disagreement: `${anchor.basis} — but config declares ${describeClaim(wantBits, wantGroup)}. Both cannot be true: the anchor is measured from tensor shapes and an architectural constant, the declaration is asserted, and neither is preferred silently.`,
    evidence: undefined,
  };
}

/** A class's propagatable answer, or `null` when its anchors do not agree. */
type PropagationSource = {
  readonly groupSize: number;
  /** `bits × groupSize` at the anchor — the scheme's fingerprint, see `resolveDrafts`. */
  readonly product: number;
  readonly basis: string;
} | null;

/**
 * Collect, per class, the group size an anchor established there.
 *
 * A source is recorded only for an anchor that actually *resolved* — an anchor
 * that the declaration contradicts left its own group unresolved, and spreading
 * evidence we just declined to act on would be worse than not propagating.
 *
 * Two anchors in one class that disagree (embedding at group 64, `lm_head` at
 * group 128, with no config entry separating them) poison that class: the
 * partition we were handed is demonstrably not the partition the repo used, so
 * nothing propagates from it. Each anchor keeps its own answer, which rests on
 * its own shapes and needs no propagation to stand.
 */
function propagationSources(
  prepared: readonly PreparedGroup[],
): Map<string, PropagationSource> {
  const sources = new Map<string, PropagationSource>();
  for (const { draft, classKey, anchor, outcome } of prepared) {
    const resolved = outcome.resolved;
    if (anchor === undefined || resolved === undefined) continue;

    const key = classKey;
    const existing = sources.get(key);
    if (existing === undefined) {
      sources.set(key, {
        groupSize: resolved.groupSize,
        product: draft.product,
        basis: `${draft.prefix} resolves to ${resolved.bits}-bit/group ${resolved.groupSize} from config hidden_size, and this module shares its declared quantization settings`,
      });
      continue;
    }
    if (
      existing !== null &&
      (existing.groupSize !== resolved.groupSize || existing.product !== draft.product)
    ) {
      sources.set(key, null);
    }
  }
  return sources;
}

/** A draft with its class, its anchor if it has one, and its first-pass resolution. */
interface PreparedGroup {
  readonly draft: AffineDraft;
  readonly classKey: string;
  readonly declared: DeclaredQuantization | undefined;
  readonly anchor: AnchorEvidence | undefined;
  readonly outcome: ResolveOutcome;
}

/**
 * Turn structurally-valid drafts into resolved groups.
 *
 * The propagation rule, stated once:
 *
 * > A group size established at an anchor propagates to another triple only when
 * > (a) the caller supplied the repo's `quantization` block, so the per-module
 * > partition is knowable at all; (b) the two triples fall in the same class of
 * > that partition; (c) that class has exactly one anchor answer; and (d) the two
 * > triples have the same `bits × groupSize` product.
 *
 * (d) is the part that is easy to skip and shouldn't be. `bits × groupSize` is a
 * property of the *scheme*, not of the tensor — every tensor quantized with the
 * same settings has the same product no matter its dimensions. So two members of
 * one class with different products are proof that they were not in fact
 * quantized alike, whatever the config's grouping implies. Where that happens,
 * nothing is propagated to the odd one out and it falls back to shapes and
 * declaration alone. A more aggressive reading would carry the group size across
 * anyway and let `bits` come out different at each end; that reading is exactly
 * how a tensor gets a confident bits-per-weight that is wrong by a factor of two.
 */
function resolveDrafts(
  drafts: readonly AffineDraft[],
  declared: DeclaredQuantization | undefined,
  anchors: ArchitectureAnchors | undefined,
): AffineGroup[] {
  const prepared: PreparedGroup[] = drafts.map((draft) => {
    const cls = classFor(draft.prefix, declared);
    const anchor = readAnchor(draft, anchors);
    return {
      draft,
      classKey: cls.key,
      declared: cls.declared,
      anchor,
      outcome: resolveOne(draft.candidates, cls.declared, anchor),
    };
  });

  // No `quantization` block means no knowable partition. Every MLX repo written
  // by `mlx_lm` has one, so this is the "config absent or silent" case rather
  // than a common path — and there, an anchor still resolves its own tensor and
  // nothing else. Assuming uniformity to reach the rest would be a guess about
  // exactly the thing per-module overrides exist to vary.
  const sources =
    declared === undefined
      ? new Map<string, PropagationSource>()
      : propagationSources(prepared);

  return prepared.map((group) => {
    const { draft } = group;
    let outcome = group.outcome;

    if (group.anchor === undefined) {
      const source = sources.get(group.classKey);
      // Condition (d): same scheme fingerprint. Once the products match, the
      // candidate lists are identical — they are a function of the product
      // alone — so the `find` cannot miss, and the guard is a type check
      // rather than a second rule.
      if (source != null && source.product === draft.product) {
        const anchorCandidate = draft.candidates.find(
          (c) => c.groupSize === source.groupSize,
        );
        if (anchorCandidate !== undefined) {
          outcome = resolveOne(draft.candidates, group.declared, {
            candidate: anchorCandidate,
            evidence: "anchor-propagated",
            basis: source.basis,
          });
        }
      }
    }

    const { resolved } = outcome;
    return {
      prefix: draft.prefix,
      weightName: draft.weightName,
      scalesName: draft.scalesName,
      biasesName: draft.biasesName,
      rows: draft.rows,
      packedCols: draft.packedCols,
      scaleCols: draft.scaleCols,
      scaleDtype: draft.scaleDtype,
      biasDtype: draft.biasDtype,
      candidates: draft.candidates,
      resolved,
      bitsPerWeight:
        resolved === undefined
          ? undefined
          : effectiveBitsPerWeight({
              bits: resolved.bits,
              groupSize: resolved.groupSize,
              scaleDtype: draft.scaleDtype,
              biasDtype: draft.biasDtype,
            }),
      disagreement: outcome.disagreement,
      evidence: outcome.evidence,
    };
  });
}

/**
 * Pick the candidate the declaration points at, or report why none can be picked.
 *
 * Three outcomes, and the middle one is the interesting one:
 *  - the declaration selects exactly one candidate → resolved, no disagreement;
 *  - the declaration selects none → unresolved *and* a disagreement message, so
 *    the caller can say "config claims X, shapes say Y" instead of picking a side;
 *  - nothing declared → resolved only when the shapes are unambiguous on their own.
 *
 * A declaration that names only `bits` or only `groupSize` is enough, since the
 * shapes pin their product.
 */
function resolveCandidate(
  candidates: readonly AffineShapeCandidate[],
  declared: DeclaredQuantization | undefined,
): { resolved: AffineShapeCandidate | undefined; disagreement: string | undefined } {
  const wantBits = declared?.bits;
  const wantGroup = declared?.groupSize;

  if (wantBits === undefined && wantGroup === undefined) {
    const only = candidates.length === 1 ? candidates[0] : undefined;
    return { resolved: only, disagreement: undefined };
  }

  const matches = candidates.filter(
    (c) =>
      (wantBits === undefined || c.bits === wantBits) &&
      (wantGroup === undefined || c.groupSize === wantGroup),
  );
  const first = matches[0];
  if (first !== undefined && matches.length === 1) {
    return { resolved: first, disagreement: undefined };
  }

  const claim = describeClaim(wantBits, wantGroup);
  const allowed = candidates
    .map((c) => `${c.bits}-bit/group ${c.groupSize}`)
    .join(" or ");

  // Reachable only via `matches.length === 0`: the candidate list is deduplicated
  // by construction (one entry per distinct bits×groupSize pair), so a filter on
  // either field can never leave two.
  return {
    resolved: undefined,
    disagreement: `config declares ${claim}, but the tensor shapes admit only ${allowed}`,
  };
}

/** "4-bit, group 64" / "4-bit" / "group 64" — however much the config committed to. */
function describeClaim(
  bits: number | undefined,
  groupSize: number | undefined,
): string {
  return [
    bits === undefined ? undefined : `${bits}-bit`,
    groupSize === undefined ? undefined : `group ${groupSize}`,
  ]
    .filter((s) => s !== undefined)
    .join(", ");
}
